import { useMutation, UseMutationOptions, UseMutationResult } from "@tanstack/react-query"
import { usePermissions } from "@/context/permissions-context"
import { toast } from "sonner"

interface ProtectedMutationOptions<TData, TError, TVariables, TContext = unknown>
  extends UseMutationOptions<TData, TError, TVariables, TContext> {
  requiredPermissions?: string[]
  requireAll?: boolean
  showToast?: boolean
}

export function useProtectedMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>({
  requiredPermissions = [],
  requireAll = true,
  showToast = true,
  mutationFn,
  ...options
}: ProtectedMutationOptions<TData, TError, TVariables, TContext>): UseMutationResult<
  TData,
  TError,
  TVariables,
  TContext
> & { hasPermission: boolean } {
  const { hasAllPermissions, hasAnyPermission } = usePermissions()

  const hasPermission = requiredPermissions.length === 0 
    ? true 
    : requireAll 
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions)

  const result = useMutation<TData, TError, TVariables, TContext>({
    mutationFn: async (variables) => {
      if (!hasPermission) {
        if (showToast) {
          toast.error("You don't have permission to perform this action")
        }
        throw new Error("PERMISSION_DENIED")
      }
      return mutationFn!(variables)
    },
    ...options,
  })

  return {
    ...result,
    hasPermission,
  }
}